import { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Package, ShoppingBag, Search, FileText, AlertTriangle, Copy, Check, RefreshCw, X, FlaskConical, Paperclip, ExternalLink } from 'lucide-react';

const GlobalMasters = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('vendors');
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [recomputingPrices, setRecomputingPrices] = useState(false);
  
  // Bill detail modal state
  const [selectedBill, setSelectedBill] = useState(null);
  const [billDetails, setBillDetails] = useState(null);
  const [loadingBillDetails, setLoadingBillDetails] = useState(false);
  
  // Delete vendor state
  // Vendor delete state removed — vendor lifecycle is owned by 1OS Bills.

  // Outlet name cache (outletId -> {name, id})
  const outletCacheRef = useRef(new Map());
  const [outletMap, setOutletMap] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  
  // Check if user is org admin
  const isOrgAdmin = user?.is_org_admin || user?.is_super_admin || user?.org_roles?.includes('ORG_ADMIN');

  // Load outlets once for mapping
  useEffect(() => {
    loadOutlets();
  }, []);

  const loadOutlets = async () => {
    // Skip if already loaded
    if (outletCacheRef.current.size > 0) {
      return;
    }
    
    try {
      const response = await api.getOutlets();
      const outlets = response.data || [];
      const map = {};
      outlets.forEach(outlet => {
        map[outlet.outlet_id] = {
          name: outlet.outlet_name || outlet.name,
          id: outlet.outlet_id
        };
        outletCacheRef.current.set(outlet.outlet_id, {
          name: outlet.outlet_name || outlet.name,
          id: outlet.outlet_id
        });
      });
      setOutletMap(map);
    } catch (error) {
      console.error('Error loading outlets for mapping:', error);
    }
  };

  const getOutletName = (outletId, bill = null) => {
    // Use backend-enriched outlet_name if available (C3 requirement)
    if (bill?.outlet_name) {
      return { name: bill.outlet_name, id: outletId };
    }
    if (!outletId) return { name: '-', id: null };
    const cached = outletMap[outletId] || outletCacheRef.current.get(outletId);
    return cached || { name: outletId, id: outletId };
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Fetch bill details from Vendor Engine
  const fetchBillDetails = async (bill) => {
    setSelectedBill(bill);
    setLoadingBillDetails(true);
    setBillDetails(null);
    
    try {
      const response = await api.getBillDetails(bill.bill_id);
      setBillDetails(response.data);
    } catch (error) {
      console.error('Error fetching bill details:', error);
      // Fallback: show basic info from the bill itself
      setBillDetails({ bill, items: [] });
    } finally {
      setLoadingBillDetails(false);
    }
  };

  const closeBillModal = () => {
    setSelectedBill(null);
    setBillDetails(null);
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'vendors') {
        // Try external vendors first (synced from external API)
        try {
          const response = await api.getExternalVendors();
          setVendors(response.data);
        } catch (e) {
          // Fallback to internal vendors
          const response = await api.getVendors();
          setVendors(response.data);
        }
      } else if (activeTab === 'items') {
        // Try external items first
        try {
          const response = await api.getExternalItems();
          setItems(response.data);
        } catch (e) {
          // Fallback to internal items
          const response = await api.getItems();
          setItems(response.data);
        }
      } else if (activeTab === 'bills') {
        // Organisation-scoped Bills API only — no local bill store fallback.
        const response = await api.getExternalBills();
        setBills(response.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (activeTab === 'vendors') {
        const response = await api.getVendors(searchQuery);
        setVendors(response.data);
      } else if (activeTab === 'items') {
        const response = await api.getItems(searchQuery);
        setItems(response.data);
      }
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'vendors', name: 'Vendors', icon: Package, count: vendors.length },
    { id: 'bills', name: 'Bills', icon: FileText, count: bills.length },
    { id: 'items', name: 'Items', icon: ShoppingBag, count: items.length },
  ];

  return (
    <div className="p-8" data-testid="masters-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Global Masters</h1>
        <p className="text-gray-600 mt-2">Manage vendors, items, and bills</p>
        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2">
          <span className="text-xs text-gray-500">
            Read-only view — vendors and items come from Control Room, bills from 1OS Bills
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={20} />
                {tab.name}
                <span
                  className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search */}
      {(activeTab === 'vendors' || activeTab === 'items' || activeTab === 'bills') && (
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                data-testid="masters-search-input"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              data-testid="masters-search-btn"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </form>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div>
          {activeTab === 'vendors' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200" data-testid="vendors-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendor Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      GST Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      City
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    {/* Vendor lifecycle (delete) is Bills-owned — no CR actions column. */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vendors.map((vendor) => (
                    <tr key={vendor.vendor_id} data-testid={`vendor-row-${vendor.vendor_id}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {vendor.vendor_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {vendor.vendor_category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.vendor_gst_number || (
                          <span className="text-gray-400">Non-GST</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.vendor_city || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.vendor_phone || '-'}
                      </td>
                      {/* Vendor delete retired — vendor identity is owned by 1OS Bills. */}
                    </tr>
                  ))}
                  {vendors.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                        No vendors found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200" data-testid="items-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ingredient ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendors
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.item_id} data-testid={`item-row-${item.item_id}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.item_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.item_brand || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.item_category_code ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                            {item.item_category_code}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.ingredient_id ? (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                            {item.ingredient_name || item.ingredient_id.substring(0, 8) + '...'}
                          </span>
                        ) : item.ingredient_mapping_status === 'needs_review' ? (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs flex items-center gap-1 w-fit">
                            <AlertTriangle size={12} /> Needs review
                          </span>
                        ) : (
                          <span className="text-orange-500">Not mapped</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <span>{item.vendor_list?.length || 0}</span>
                          {/* Inline data quality indicators */}
                          {item.quantity_source && item.quantity_source.toLowerCase().includes('fallback') && (
                            <span className="ml-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded text-[10px]" title="Fallback quantity used">
                              Fallback
                            </span>
                          )}
                          {!item.sku_size && !(/\d+\s*(kg|g|gm|ml|l|ltr)/i.test(item.item_name || '')) && (
                            <span className="ml-1 px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[10px]" title="No SKU size detected">
                              No SKU
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'bills' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200" data-testid="bills-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bill Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Outlet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trial
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Image
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bills.map((bill) => {
                    const outlet = getOutletName(bill.outlet_id, bill);
                    const amount = bill.bill_amount || bill.total_amount || 0;
                    return (
                    <tr 
                      key={bill.bill_id} 
                      data-testid={`bill-row-${bill.bill_id}`}
                      onClick={() => fetchBillDetails(bill)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {bill.bill_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {bill.vendor_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {outlet.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {bill.bill_date ? new Date(bill.bill_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {amount ? `₹${amount.toLocaleString()}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {bill.is_trial_bill ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                            <FlaskConical size={12} />
                            Yes
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {bill.not_in_expense_app ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium" title="This bill is no longer present in the Expense App">
                            <AlertTriangle size={12} />
                            Not in Expense App
                          </span>
                        ) : bill.is_deleted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                            Deleted
                          </span>
                        ) : (
                          <span className="text-green-600 text-xs">Synced</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {bill.bill_image_url ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(bill.bill_image_url, '_blank', 'noopener,noreferrer');
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 transition-colors"
                            title="View bill image"
                          >
                            <Paperclip size={12} />
                            Image
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  );
                  })}
                  {bills.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                        No bills found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}


      {/* Bill Detail Modal */}
      {selectedBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Bill Details</h3>
                <p className="text-sm text-gray-500">#{selectedBill.bill_number}</p>
              </div>
              <button
                onClick={closeBillModal}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingBillDetails ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : billDetails ? (
                <>
                  {/* Bill Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Vendor</p>
                      <p className="font-medium">{billDetails.bill?.vendor_name || selectedBill.vendor_name || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Date</p>
                      <p className="font-medium">
                        {(billDetails.bill?.bill_date || selectedBill.bill_date) 
                          ? new Date(billDetails.bill?.bill_date || selectedBill.bill_date).toLocaleDateString() 
                          : '-'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Total Amount</p>
                      <p className="font-medium text-green-600">
                        ₹{(billDetails.bill?.total_amount || selectedBill.total_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Status</p>
                      <p className="font-medium">{billDetails.bill?.status || selectedBill.status || '-'}</p>
                    </div>
                  </div>

                  {/* Bill Image Section - Opens URL directly (no XHR to avoid CORS) */}
                  {(billDetails.bill?.bill_image_url || selectedBill.bill_image_url) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Paperclip className="text-blue-600" size={20} />
                          <div>
                            <h4 className="font-semibold text-blue-900">Bill Image Attached</h4>
                            {(billDetails.bill?.bill_image_uploaded_at || selectedBill.bill_image_uploaded_at) && (
                              <p className="text-xs text-blue-600">
                                Uploaded: {new Date(billDetails.bill?.bill_image_uploaded_at || selectedBill.bill_image_uploaded_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => window.open(billDetails.bill?.bill_image_url || selectedBill.bill_image_url, '_blank', 'noopener,noreferrer')}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          <ExternalLink size={16} />
                          View Image
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Trial Info Section */}
                  {(billDetails.bill?.is_trial_bill || selectedBill.is_trial_bill) && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <FlaskConical className="text-purple-600" size={20} />
                        <h4 className="font-semibold text-purple-900">Trial Bill Information</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-purple-600">Trial Total Spend</p>
                          <p className="text-lg font-bold text-purple-900">
                            ₹{(billDetails.bill?.trial_total_amount || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-purple-600">Trial Items</p>
                          <p className="text-lg font-bold text-purple-900">
                            {billDetails.items?.filter(i => i.is_trial_item).length || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Line Items */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Line Items</h4>
                    {billDetails.items && billDetails.items.length > 0 ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Qty</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">MRP</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Unit Price</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Discount</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Total</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Trial</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {billDetails.items.map((item, idx) => {
                              const mrp = item.item_mrp || item.mrp || 0;
                              const unitPrice = item.item_unit_price || 0;
                              const discountPercent = mrp > 0 && unitPrice < mrp 
                                ? ((mrp - unitPrice) / mrp * 100).toFixed(1) 
                                : 0;
                              return (
                              <tr key={idx} className={item.is_trial_item ? 'bg-purple-50' : ''}>
                                <td className="px-4 py-2 text-sm">
                                  {item.item_description || item.ingredient_name || '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  {item.item_quantity} {item.item_unit}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  {mrp > 0 ? `₹${mrp.toFixed(2)}` : '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  ₹{unitPrice.toFixed(2)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {discountPercent > 0 ? (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                                      {discountPercent}% off
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 text-xs">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm font-medium">
                                  ₹{(item.item_line_total_incl_tax || item.item_line_total_excl_tax || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  {item.is_trial_item ? (
                                    <div>
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs">
                                        <FlaskConical size={10} />
                                        Trial
                                      </span>
                                      {item.trial_amount > 0 && (
                                        <p className="text-xs text-purple-600 mt-1">
                                          ₹{item.trial_amount.toFixed(2)}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">-</span>
                                  )}
                                </td>
                              </tr>
                            );})}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No line items available</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-gray-500">Unable to load bill details</p>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeBillModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalMasters;
